import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Drawer,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
  Typography,
  Divider,
  Avatar,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Article as ArticleIcon,
  School as SchoolIcon,
  People as PeopleIcon,
  Person as PersonIcon,
  Notes as NotesIcon,
  Settings as SettingsIcon,
  Image as ImageIcon,
  BugReport as BugReportIcon,
  ExpandLess,
  ExpandMore,
  Layers as LayersIcon,
  ManageAccounts as ManageAccountsIcon,
  Backup as BackupIcon,
} from '@mui/icons-material';
import { NavItem } from '../../../types/admin.types';
import ConfigManagementService, { ConfigScheme } from '../../../services/ConfigManagementService';
import AuthService from '../../../services/AuthService';
import logo from '../../../assets/logo.png';

interface AdminSidebarProps {
  open: boolean;
  onClose: () => void;
}

const NAV_ITEMS: NavItem[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: 'dashboard',
    path: '/admin',
  },
  {
    id: 'users',
    label: 'Users',
    icon: 'person',
    path: '/admin/users',
  },
  {
    id: 'roles',
    label: 'Role Management',
    icon: 'roles',
    path: '/admin/roles',
  },
  {
    id: 'media',
    label: 'Media Management',
    icon: 'image',
    path: '/admin/media',
  },
  {
    id: 'config',
    label: 'Configuration Management',
    icon: 'settings',
    path: '/admin/configs',
  },
  {
    id: 'backup',
    label: 'Backup & Restore',
    icon: 'backup',
    path: '/admin/backup',
  },
];

const getIconComponent = (iconName: string) => {
  const icons: { [key: string]: React.ReactNode } = {
    dashboard: <DashboardIcon />,
    article: <ArticleIcon />,
    school: <SchoolIcon />,
    people: <PeopleIcon />,
    person: <PersonIcon />,
    notes: <NotesIcon />,
    image: <ImageIcon />,
    test: <BugReportIcon />,
    settings: <SettingsIcon />,
    roles: <ManageAccountsIcon />,
    backup: <BackupIcon />,
  };
  return icons[iconName] || <DashboardIcon />;
};

export const AdminSidebar: React.FC<AdminSidebarProps> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [expandedItems, setExpandedItems] = React.useState<Set<string>>(new Set());
  const [pinnedSchemes, setPinnedSchemes] = React.useState<ConfigScheme[]>([]);

  // Get current user for role-based filtering
  const currentUser = AuthService.getUser();
  const isAdminUser = currentUser?.role === 'admin';

  // Filter navigation items based on user role
  const filteredNavItems = React.useMemo(
    () => NAV_ITEMS.filter((item) => {
      // Hide config management for non-admin users
      if (item.id === 'config' && !isAdminUser) {
        return false;
      }
      if (item.id === 'backup' && !isAdminUser) {
        return false;
      }
      return true;
    }),
    [isAdminUser]
  );

  // Load pinned schemes on mount and refresh when navigating back to admin
  React.useEffect(() => {
    ConfigManagementService.getAllSchemes()
      .then((schemes) => setPinnedSchemes(schemes.filter((s) => s.pinToSidebar)))
      .catch(() => {});
  }, []);

  const handleExpandClick = (id: string) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedItems(newExpanded);
  };

  const handleNavItemClick = (path: string) => {
    navigate(path);
    if (isMobile) {
      onClose();
    }
  };

  const isItemActive = (itemPath: string) => {
    return location.pathname.startsWith(itemPath) && itemPath !== '/admin';
  };

  const NavItemComponent: React.FC<{ item: NavItem; level?: number }> = ({ item, level = 0 }) => {
    const isExpanded = expandedItems.has(item.id);
    const hasChildren = item.children && item.children.length > 0;
    const isActive = isItemActive(item.path);

    return (
      <React.Fragment key={item.id}>
        <ListItem
          button
          onClick={() => {
            if (hasChildren) {
              handleExpandClick(item.id);
            } else {
              handleNavItemClick(item.path);
            }
          }}
          title={!open ? item.label : undefined}
          sx={{
            mb: 0.3,
            py: 0.7,
            minHeight: 36,
            pl: open ? level * 1.5 + 2 : 1,
            backgroundColor: level === 0 ? 'transparent' : (isActive ? '#005e5d14' : 'transparent'),
            color: isActive ? '#005e5d' : '#666',
            transition: 'all 0.2s ease',
            '&:hover': {
              backgroundColor: level === 0 ? 'transparent' : (isActive ? '#005e5d14' : '#f5f5f5'),
              color: isActive ? '#005e5d' : '#333',
            },
            borderLeft: isActive ? '3px solid #005e5d' : '3px solid transparent',
            justifyContent: open ? 'flex-start' : 'center',
          }}
        >
          <ListItemIcon
            sx={{
              color: 'inherit',
              minWidth: open ? 32 : 'auto',
              fontSize: '16px',
              justifyContent: 'center',
              opacity: isActive ? 1 : 0.45,
            }}
          >
            {getIconComponent(item.icon)}
          </ListItemIcon>
          {open && (
            <>
              <ListItemText
                primary={
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: isActive ? 700 : 500,
                      fontSize: '13px',
                      color: 'inherit',
                    }}
                  >
                    {item.label}
                  </Typography>
                }
                sx={{ my: 0, ml: 0.5 }}
              />
              {hasChildren && (
                <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }}>
                  {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                </Box>
              )}
            </>
          )}
        </ListItem>

        {open && hasChildren && item.children && (
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {item.children.map((child) => (
                <NavItemComponent key={child.id} item={child} level={level + 1} />
              ))}
            </List>
          </Collapse>
        )}
      </React.Fragment>
    );
  };

  const drawerContent = (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: '#ffffff', borderRight: '1px solid #E6E7EB' }}>
      {/* Header */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: '1px solid #E6E7EB',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          height: '64px',
        }}
      >
        {open && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
            <Box
              component="img"
              src={logo}
              alt="Logo"
              sx={{
                width: 40,
                height: 40,
                borderRadius: '8px',
                objectFit: 'contain',
                flexShrink: 0,
              }}
            />
            <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: 13,
                  letterSpacing: '-0.01em',
                  lineHeight: 1.2,
                  color: '#333333',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                Admin Manager
              </Typography>
              <Typography sx={{ fontWeight: 500, fontSize: 10, color: '#999', lineHeight: 1.3 }}>
                Jennifer English Center
              </Typography>
            </Box>
          </Box>
        )}
        {!open && (
          <Box
            component="img"
            src={logo}
            alt="Logo"
            sx={{ width: 36, height: 36, borderRadius: '8px', objectFit: 'contain', mx: 'auto' }}
          />
        )}
        {open && (
          <Box
            sx={{
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '6px',
              backgroundColor: '#f5f5f5',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: '#efefef',
              },
            }}
            onClick={onClose}
            title="Collapse"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="15 18 9 12 15 6"></polyline>
            </svg>
          </Box>
        )}
      </Box>

      {/* Navigation */}
      <List
        component="nav"
        dense
        disablePadding
        sx={{
          flex: 1,
          py: 1,
          px: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          '&::-webkit-scrollbar': {
            width: '5px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#ddd',
            borderRadius: '3px',
            '&:hover': {
              backgroundColor: '#bbb',
            },
          },
        }}
      >
        {filteredNavItems.map((item) => (
          <NavItemComponent key={item.id} item={item} />
        ))}

        {/* Dynamic Manager section - pinned schemas */}
        {pinnedSchemes.length > 0 && (
          <>
            <Divider sx={{ my: 1, borderColor: '#E6E7EB' }} />
            {open && (
              <Typography
                sx={{
                  fontSize: '10px',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  color: '#999',
                  px: 2,
                  mb: 0.5,
                  mt: 0.5,
                }}
              >
                Manager
              </Typography>
            )}
            {pinnedSchemes.map((scheme) => {
              const path = `/admin/manager/${scheme.key}`;
              const isActive = location.pathname === path;
              return (
                <ListItem
                  key={scheme.key}
                  button
                  onClick={() => {
                    navigate(path);
                    if (isMobile) onClose();
                  }}
                  title={!open ? (scheme.label || scheme.key) : undefined}
                  sx={{
                    mb: 0.3,
                    py: 0.7,
                    minHeight: 36,
                    pl: open ? 2 : 1,
                    backgroundColor: isActive ? '#005e5d14' : 'transparent',
                    color: isActive ? '#005e5d' : '#666',
                    transition: 'all 0.2s ease',
                    borderLeft: isActive ? '3px solid #005e5d' : '3px solid transparent',
                    borderRadius: '0 8px 8px 0',
                    '&:hover': {
                      backgroundColor: isActive ? '#005e5d14' : '#f5f5f5',
                      color: isActive ? '#005e5d' : '#333',
                    },
                    justifyContent: open ? 'flex-start' : 'center',
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: 'inherit',
                      minWidth: open ? 32 : 'auto',
                      justifyContent: 'center',
                      opacity: isActive ? 1 : 0.45,
                    }}
                  >
                    <LayersIcon sx={{ fontSize: 18 }} />
                  </ListItemIcon>
                  {open && (
                    <ListItemText
                      primary={
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: isActive ? 700 : 500,
                            fontSize: '13px',
                            color: 'inherit',
                          }}
                        >
                          {scheme.label || scheme.key}
                        </Typography>
                      }
                      secondary={
                        scheme.description ? (
                          <Typography
                            variant="caption"
                            sx={{ fontSize: '11px', color: '#999', lineHeight: 1 }}
                          >
                            {scheme.description}
                          </Typography>
                        ) : undefined
                      }
                      sx={{ my: 0, ml: 0.5 }}
                    />
                  )}
                </ListItem>
              );
            })}
          </>
        )}
      </List>

      {/* Bottom Section */}
     
    </Box>
  );

  return (
    <>
      {/* Desktop Sidebar */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          width: open ? 280 : 0,
          flexShrink: 0,
          transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '& .MuiDrawer-paper': {
            width: open ? 280 : 0,
            boxSizing: 'border-box',
            backgroundColor: '#ffffff',
            borderRight: '1px solid #E6E7EB',
            top: 0,
            height: '100vh',
            overflow: 'hidden',
            transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Mobile Sidebar */}
      <Drawer
        variant="temporary"
        anchor="left"
        open={open}
        onClose={onClose}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
          },
          '& .MuiDrawer-paper': {
            width: 280,
            boxSizing: 'border-box',
            backgroundColor: '#ffffff',
            borderRight: '1px solid #E6E7EB',
          },
        }}
      >
        {drawerContent}
      </Drawer>
    </>
  );
};

export default AdminSidebar;
